import { I18n } from "react-redux-i18n";
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';;

import { getTestAttributes } from "@common/utils/tests";
import { Flex } from "@common/components/Flex";
import { Button } from "@common/components/Button";
import { Loading } from "@common/components/loading";
import { MODULE_NAME } from "@modules/individuals";
import { Questionnaires } from "../questionnaires";
import { getQuestionnaires, getQuestionnaireForInvite } from "../../actions/questionnaires";
import { EmailTemplate } from "./components/email-template";
import { getIndividualEmail, saveEmailForIndividual, getIndividualFamilyData, getFamilyIdMembersCount } from "../../actions/individuals";
import { EmailAddress } from "./components/email-address";
import { isEmail } from '@common/utils/email';
import { PedigreeFolder } from "./components/pedigree-folder";
import { checkPedigreeNameExists, createPedigreeAndMoveToFolder, getPedigreeFolders } from "../../actions/pedigrees";

import "./new-invite.styles.scss";

export const NEW_INVITE_STEPS = {
  questionnaires: "questionnaires",
  emailTemplate: "emailTemplate",
  emailAddress: "emailAddress",
  pedigreeFolder: "pedigreeFolder",
};
export const NEW_INVITE_QUESTIONNAIRE_TYPE = {
  individual: 'INDIVIDUAL',
  family: 'FAMILY'
};

export const NewInvite = (props) => {
  const localModuleName = `${MODULE_NAME}-new-invite`;
  const { onClose, onSubmit, showErrorMessage, showInfoMessage, individualId, inviteId, getSteps, sendInvite } = props;
  const [currentStep, setCurrentStep] = React.useState(NEW_INVITE_STEPS.questionnaires);
  const [submitting, setSubmitting] = React.useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = React.useState();
  const [emailSubject, setEmailSubject] = React.useState('Family History Questionnaire');
  const [emailTemplate, setEmailTemplate] = React.useState('');
  const [loadingEmailState, setLoadingEmailState] = React.useState(false);
  const [hasSavedEmail, setSavedEmailState] = React.useState(false);
  const [emailAddress, setEmailAddress] = React.useState('');
  const [individualFamilyData, setIndividualFamilyData] = React.useState();
  const [initializingComponent, setInitializingComponentState] = React.useState(true);
  const [individualFamilyMembersCount, setIndividualFamilyMembersCount] = React.useState();
  const [dataToMoveIndividual, setDataToMoveIndividual] = React.useState({ email: '', folderId: null });

  const handleSelectedQuestionnaireChange = React.useCallback((item) => {
    setSelectedQuestionnaire(item);
    setEmailTemplate(item.emailTemplate.body);
    setEmailSubject(item.emailTemplate.subject);
  }, []);

  React.useEffect(() => {
    if (inviteId) {
      getQuestionnaireForInvite(inviteId).then((data) => {
        setSelectedQuestionnaire(data);
        setEmailTemplate(data.reminderEmailTemplate?.body);
        setEmailSubject(data.reminderEmailTemplate?.subject);
      })
    } else {
      getIndividualFamilyData(individualId).then((data) => {
        setIndividualFamilyData(data);

        getFamilyIdMembersCount(data.familyId).then((count) => {
          setIndividualFamilyMembersCount(count);
          setInitializingComponentState(false);
        });
      });
    }
  }, []);

  React.useEffect(() => {
    if (selectedQuestionnaire) {
      setLoadingEmailState(true);
      getIndividualEmail({ wdcfolderId: selectedQuestionnaire.wdcFolderId, globalId: individualId })
        .then((email) => {
          if (email?.length && isEmail(email)) {
            setSavedEmailState(true);
          } else {
            const notValidEmail = email || '';

            setEmailAddress(notValidEmail);
          }

          setLoadingEmailState(false);
        })
        .catch((err) => {
          console.error(err);
          setLoadingEmailState(false);
        });
    }
  }, [selectedQuestionnaire]);

  React.useEffect(() => {
    if (inviteId && !loadingEmailState) {
      setCurrentStep(getNextStepFromQuestionnaires());
      setInitializingComponentState(false);
    }
  }, [loadingEmailState]);


  const saveEmail = React.useCallback(async () => {
    try {
      setSubmitting(true);

      await saveEmailForIndividual({
        questionnaireId: selectedQuestionnaire.questionnaireId,
        wdcfolderId: selectedQuestionnaire.wdcFolderId,
        globalId: props.individualId,
        email: emailAddress
      });
      setSavedEmailState(true);
      setSubmitting(false);
      
      return true;
    } catch (err) {
      setSubmitting(false);
      showErrorMessage(err?.entity || I18n.t("Operation failed"));
      console.error(err);

      return false;
    }
  }, [props.individualId, selectedQuestionnaire, emailAddress]);
  
  const validateSelectedQuestionnaire = React.useCallback(() => {
    if (Boolean(selectedQuestionnaire?.datasheetFormatName?.length) || selectedQuestionnaire.fhq4) {
      return true;
    }

    showInfoMessage(I18n.t("Selected questionnaire doesn't have Datasheet Format"));

    return false;
  }, [showInfoMessage, selectedQuestionnaire]);

  const createPedigree = React.useCallback(async () => {
    try {
      const exists = await checkPedigreeNameExists(dataToMoveIndividual.folderId, dataToMoveIndividual.email);

      if (exists) {
        showInfoMessage(I18n.t("The Pedigree Name you entered is already used in this database, please choose another name"));

        return false;
      }
    } catch (err) {
      console.error(err);
      showInfoMessage(err.entity || I18n.t("The Pedigree Name you entered is already used in this database, please choose another name"));

      return false;
    }
    
    try {
      await createPedigreeAndMoveToFolder({
        pedigreeName: dataToMoveIndividual.email,
        pedigreeFolderId: dataToMoveIndividual.folderId,
        familyId: individualFamilyData.familyId,
      });

      const data = await getIndividualFamilyData(individualId);
      setIndividualFamilyData(data);

      const count = getFamilyIdMembersCount(data.familyId);
      setIndividualFamilyMembersCount(count);

      return true;
    } catch (err) {
      console.error(err);
      showErrorMessage(err.entity || I18n.t("Failed to create pedigree"));

      return false;
    }
  }, [showInfoMessage, dataToMoveIndividual, individualFamilyData?.familyId]);

  const getNextStepFromQuestionnaires = (quest = selectedQuestionnaire) => {
    if (getSteps) {
      const steps = getSteps({
        questionnaire: quest,
        hasSavedEmail,
        individualFamilyData,
      });

      return steps.find((x) => x.condition)?.step;
    }

    const config = [
      {
        condition: quest?.fhq4 && hasSavedEmail,
        step: NEW_INVITE_STEPS.emailTemplate,
      },
      {
        condition: quest?.fhq4 && !hasSavedEmail,
        step: NEW_INVITE_STEPS.emailAddress,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && !individualFamilyData.familyName,
        step: NEW_INVITE_STEPS.pedigreeFolder,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && individualFamilyData.familyName && !hasSavedEmail,
        step: NEW_INVITE_STEPS.emailAddress,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && individualFamilyData.familyName && hasSavedEmail,
        step: NEW_INVITE_STEPS.emailTemplate,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.individual && !hasSavedEmail,
        step: NEW_INVITE_STEPS.emailAddress,
      },
      {
        condition: quest?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.individual && hasSavedEmail,
        step: NEW_INVITE_STEPS.emailTemplate,
      },
    ];

    return config.find((x) => x.condition)?.step;
  };

  const getNextStepFromPedigreeFolder = () => {
    const config = [
      {
        condition: selectedQuestionnaire?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && !hasSavedEmail,
        step: NEW_INVITE_STEPS.emailAddress,
      },
      {
        condition: selectedQuestionnaire?.questionnaireType === NEW_INVITE_QUESTIONNAIRE_TYPE.family && hasSavedEmail,
        step: NEW_INVITE_STEPS.emailTemplate,
      },
    ];

    return config.find((x) => x.condition)?.step;
  };

  const stepsConfig = {
    [NEW_INVITE_STEPS.questionnaires]: {
      buttonProps: {
        disabled: selectedQuestionnaire === undefined || (selectedQuestionnaire && loadingEmailState),
      },
      component: (
        <Questionnaires 
          getData={() => getQuestionnaires({
            includeFamilyQuestionnaires: individualFamilyMembersCount > 1 ? false : true,
            includeFhq4Questionnaires: true,
          })} 
          title={I18n.t("Select a Questionnaire you want to invite patients to")}
          selectedQuestionnaire={selectedQuestionnaire} 
          onSelectedQuestionnaireChange={handleSelectedQuestionnaireChange}
        />
      ),
      executeBeforeGoNextStep: validateSelectedQuestionnaire,
      nextStep: getNextStepFromQuestionnaires(),
    },
    [NEW_INVITE_STEPS.pedigreeFolder]: {
      buttonProps: {
        disabled: !dataToMoveIndividual.email || !dataToMoveIndividual.folderId
      },
      component: (
        <PedigreeFolder
          getData={getPedigreeFolders} 
          email={dataToMoveIndividual.email} 
          folderId={dataToMoveIndividual.folderId}
          onChange={setDataToMoveIndividual}
        />
      ),
      executeBeforeGoNextStep: createPedigree,
      prevStep: NEW_INVITE_STEPS.questionnaires,
      nextStep: getNextStepFromPedigreeFolder(),
    },
    [NEW_INVITE_STEPS.emailAddress]: {
      buttonProps: {
        disabled: !emailAddress || !isEmail(emailAddress),
      },
      component: (
        <EmailAddress 
          defaultValue={emailAddress}
          onChange={setEmailAddress}
        />
      ),
      executeBeforeGoNextStep: saveEmail,
      prevStep: NEW_INVITE_STEPS.questionnaires,
      nextStep: NEW_INVITE_STEPS.emailTemplate,
    },
    [NEW_INVITE_STEPS.emailTemplate]: {
      component: (
        <EmailTemplate 
          subject={emailSubject}
          onSubjectChange={setEmailSubject}
          template={emailTemplate}
          onTemplateChange={setEmailTemplate}
        />
      ),
      prevStep: NEW_INVITE_STEPS.questionnaires,
      submitBtnText: inviteId ? I18n.t('Send a Reminder')  : I18n.t('Send an Invite')
    },
  };
  const currentStepConfig = stepsConfig[currentStep];

  const handleGoToPrevStep = React.useCallback((e) => {
    setEmailAddress('');
    setDataToMoveIndividual({ email: '', folderId: null });
    setCurrentStep(currentStepConfig.prevStep);
  }, [currentStepConfig?.prevStep]);

  const handleGoToNextStep = async (e) => {
    if (currentStepConfig.executeBeforeGoNextStep) {
      const isSuccess = await currentStepConfig.executeBeforeGoNextStep();

      if (!isSuccess) {
        return;
      }
    }

    setCurrentStep(currentStepConfig?.nextStep);
  };

  const handleSubmit = React.useCallback(async () => {
    setSubmitting(true);

    try {
      await sendInvite({
        questionnaireId: selectedQuestionnaire.questionnaireId,
        wdcfolderId: selectedQuestionnaire.wdcFolderId,
        globalId: props.individualId,
        emailTemplate: {
          ...selectedQuestionnaire.emailTemplate,
          body: emailTemplate,
          subject: emailSubject,
        },
        fhq4: selectedQuestionnaire.fhq4,
      });
      onSubmit();
    } catch (err) {
      setSubmitting(false);
      showErrorMessage(err?.entity || I18n.t("Operation failed"));
      console.error(err);
    }
  }, [emailSubject, emailTemplate, props.individualId, selectedQuestionnaire, onSubmit, showErrorMessage]);

  return (
    <Modal
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="IndividualsNewInvite"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes(localModuleName, "header", "title")}
        >
          {I18n.t("Invite Patient to the Questionnaire")}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        {initializingComponent || typeof currentStepConfig == 'undefined'
          ? <Flex className="IndividualsNewInvite__loading" justify="center" alignItems="center"><Loading size={30} /></Flex>
          : currentStepConfig.component
        }
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={submitting}
          {...getTestAttributes(MODULE_NAME, "cancel", "button")}
          onClick={onClose}
        >
          {I18n.t("Cancel")}
        </Button>
        <Flex>
          {currentStepConfig?.prevStep && (
            <Button
              className="IndividualsNewInvite__previous-btn"
              disabled={submitting}
              {...getTestAttributes(localModuleName, "submit", "button")}
              onClick={handleGoToPrevStep}
            >
              {I18n.t("Previous")}
            </Button>
          )}
          <Button
            disabled={submitting}
            bsStyle="primary"
            {...getTestAttributes(localModuleName, "submit", "button")}
            {...currentStepConfig?.buttonProps}
            onClick={currentStepConfig?.nextStep ? handleGoToNextStep : handleSubmit}
          >
            {submitting ? I18n.t("Loading") : I18n.t(currentStepConfig?.submitBtnText || "Next")}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
